import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { Sparkles, Target, Briefcase } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { tierLabelOf } from '@/shared/permissions/identity';
import { LeadTriagePanel } from '../components/LeadTriagePanel';
import { DealCoachPanel } from '../components/DealCoachPanel';
import { DEPARTMENT_PERSONA } from '../constants';
import { EmptyState } from '@/shared/components/ui/EmptyState';

type TabKey = 'triage' | 'coach';

export function AICopilotPage() {
  const user = useUser();
  const [tab, setTab] = useState<TabKey>('triage');

  const dept = user?.department || 'sales';
  const persona = DEPARTMENT_PERSONA[dept as keyof typeof DEPARTMENT_PERSONA];

  if (!persona && dept !== 'superadmin' && user?.role !== 'superadmin') {
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">AI Copilot</h1>
          </div>
        </div>
        <EmptyState
          title="Not Available"
          description="AI Copilot is not yet available for your department."
        />
      </div>
    );
  }

  const activePersona = persona || DEPARTMENT_PERSONA['sales'];
  
  const TABS = [
    { key: 'triage' as TabKey, label: `${activePersona.triageEntity} Triage`, icon: Target },
    { key: 'coach' as TabKey, label: `${activePersona.coachEntity} Coach`, icon: Briefcase },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={Sparkles}
        eyebrow="Workspace"
        title="AI Copilot"
        description={<>{tierLabelOf(user?.role)} view · {activePersona.triageEntity.toLowerCase()} triage and {activePersona.coachEntity.toLowerCase()} coaching. Every output is a labeled,
            traceable AI suggestion — not a persisted CRM value.</>}
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-outline">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-2 text-data-md font-medium transition-colors',
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface',
              )}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panels — mounted only when active so each fetches on demand. */}
      <div className="flex-1">
        {tab === 'triage' && <LeadTriagePanel enabled={tab === 'triage'} />}
        {tab === 'coach' && <DealCoachPanel enabled={tab === 'coach'} />}
      </div>
    </div>
  );
}
